import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import './Homepage.css';
import RectangularLogo from './assets/rectangular-logo.png';
import Slide1 from './assets/slide1.png';
import Slide2 from './assets/slide2.png';
import Slide3 from './assets/slide3.png';

const API_BASE_URL = "https://jpf8ds43gf.execute-api.us-west-2.amazonaws.com/prod";
const AUTH_ENDPOINT = `${API_BASE_URL}/auth`;
const POLL_ENDPOINT = `${API_BASE_URL}/poll`;
const REDIRECT_ENDPOINT = `${API_BASE_URL}/redirect`;

// Validate email format
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

function Homepage() {
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateMessage, setGenerateMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [faqOpen, setFaqOpen] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pollingIntervalRef = useRef(null);
  const hasAuthenticatedRef = useRef(false);

  const slides = [Slide1, Slide2, Slide3];

  // Slideshow effect for SizzleWrap preview
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
    }, 3000);
    return () => clearInterval(slideInterval);
  }, [slides.length]);

  // Clear any active polling interval
  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    pollingIntervalRef.current = null;
    setIsGenerating(false);
  }, []);

  // Poll the API to check on the generation status
  const pollForGeneration = useCallback(async (uid) => {
    try {
      const response = await fetch(POLL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: uid }),
      });
      const data = await response.json();
      if (data.message === "READY") {
        setGenerateMessage("Generation Complete! 🎉");
        clearPolling();
      } else if (data.message === "ERROR") {
        setGenerateMessage("Whoops! An error occurred, please try again. If error persists, please see FAQ for more information.");
        clearPolling();
      } else if (data.message === "INSUFFICIENT_POSTS") {
        setGenerateMessage("Your instagram does not have enough posts to generate a SizzleWrap :( We can only generate a slideshow with accounts with more than 10 total posts.");
        clearPolling();
      } else {
        setGenerateMessage("Generation usually takes 5–10 mins. You can hang out here, or you will receive an email when it's complete!");
      }
    } catch {
      setGenerateMessage("An error occurred. Try again.");
      clearPolling();
    }
  }, [clearPolling]);

  // Start polling every 5 seconds
  const startPollingInterval = useCallback((uid) => {
    pollingIntervalRef.current = setInterval(() => pollForGeneration(uid), 5000);
  }, [pollForGeneration]);

  // Handle the Generate button click
  const handleGenerate = useCallback(() => {
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    const code = searchParams.get("code");
    if (!code) {
      localStorage.setItem("userEmail", email);
      window.location.href = REDIRECT_ENDPOINT;
    }
    // If a code exists, the authentication useEffect below will handle it.
  }, [email, searchParams]);

  // Handle Instagram OAuth callback and user authentication
  useEffect(() => {
    const code = searchParams.get("code");
    if (!code || hasAuthenticatedRef.current) return;

    const storedEmail = localStorage.getItem("userEmail");
    if (!storedEmail) {
      setEmailError("Email not found. Please try again.");
      return;
    }

    const authenticateUser = async () => {
      setIsGenerating(true);
      try {
        const authResponse = await fetch(AUTH_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ instagram_code: code, email: storedEmail }),
        });
        const authData = await authResponse.json();
        if (!authData.user_id) {
          setGenerateMessage("Authentication failed. Please try again.");
          setIsGenerating(false);
          return;
        }
        setUserId(authData.user_id);
        hasAuthenticatedRef.current = true;
        localStorage.setItem("authenticated", "true");

        // Remove the 'code' parameter from the URL to avoid re-processing
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("code");
        navigate({ search: newSearchParams.toString() }, { replace: true });

        await pollForGeneration(authData.user_id);
        startPollingInterval(authData.user_id);
      } catch {
        setGenerateMessage("Authentication error. Try again.");
        setIsGenerating(false);
      }
    };

    authenticateUser();
  }, [searchParams, navigate, pollForGeneration, startPollingInterval]);

  // New useEffect: Remove the "code" param if the user is already authenticated.
  useEffect(() => {
    if (localStorage.getItem("authenticated") === "true") {
      const code = searchParams.get("code");
      if (code) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("code");
        navigate({ search: newSearchParams.toString() }, { replace: true });
      }
    }
  }, [searchParams, navigate]);

  // Clear polling when the component unmounts
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  // Allows the user to view their generated content immediately
  const viewNow = () => {
    if (!userId) {
      setEmailError("User ID not available.");
      return;
    }
    navigate(`/${encodeURIComponent(userId)}`);
  };

  // FAQ items for the homepage
  const faqItems = [
    { question: "How does SizzleWrap work?", answer: "We take posts off your Instagram profile over the last year, run them through an image recognition engine, and create a fun slideshow using AI. Humor included!" },
    { question: "How long does it take to generate?", answer: "Usually within 5 mins after connecting your account." },
    { question: "How much does SizzleWrap cost?", answer: "Right now, SizzleWrap is free to use!" },
    { question: "Does SizzleWrap do anything with my data?", answer: "Not at all! Data gets deleted after slideshow generation and is not used or sold anywhere." },
    { 
      question: "Why am I getting authorization errors?", 
      answer: (
        <>
          <strong>Important:</strong> If you receive an error during account connection, it might be because your Instagram account is not public. For more details on how to set your account to public, please visit the <Link to="/make-account-public">Make Account Public</Link> page.
        </>
      )
    },
    { question: "I am continuosly seeing 'error while generating'. What do I do?", answer: "Email help@sizzlewrap.com" },
    { question: "I have other problems. What do I do?", answer: "Email help@sizzlewrap.com" }
  ];

  return (
    <div className="homepage-container">
      <main className="content">
        <img src={RectangularLogo} alt="Logo" className="main-logo" />
        <h1 className="title">Your Instagram 🔥 Wrapped & Revealed</h1>
        {/* Instructions Section with Bold Note */}
        <section className="instructions-section">
          <h2>Instructions</h2>
          <ol>
            <li>Enter your email</li>
            <li>Connect your Instagram</li>
            <li>Receive a slideshow to your email in 5-10 minutes</li>
          </ol>
          <p><strong>NOTE:</strong> You must have a public Instagram account for SizzleWrap to work. You can enable this temporarily, see <Link to="/make-account-public">Make Account Public</Link> page for more info.</p>
        </section>
        <div className="form-section">
          <input
            type="email"
            className="email-input"
            placeholder="your-email@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
            disabled={isGenerating}
          />
          {emailError && <span className="error-message">{emailError}</span>}
          <button className="generate-btn" onClick={handleGenerate} disabled={isGenerating || !email}>
            {isGenerating ? "Generating..." : "Connect Instagram and Generate"}
          </button>
        </div>
        {generateMessage && <p className="generate-message">{generateMessage}</p>}
        {generateMessage.includes("Complete") && <button className="view-now-btn" onClick={viewNow}>View Now</button>}
        <section className="slideshow-section">
          <h2>SizzleWrap at a Glance</h2>
          <img src={slides[currentSlide]} alt="SizzleWrap preview" className="slideshow-image" />
        </section>
        <section className="faq-section">
          {faqItems.map((item, i) => (
            <div key={i} className="faq-item">
              <button className="faq-question" onClick={() => setFaqOpen(faqOpen === i ? null : i)}>
                {item.question}
              </button>
              {faqOpen === i && <div className="faq-answer">{item.answer}</div>}
            </div>
          ))}
        </section>
      </main>
      <footer className="footer">&copy; 2025 Bytenest LLC. All rights reserved.</footer>
    </div>
  );
}

export default Homepage;
